import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { debounceTime, filter, map, switchMap, tap } from 'rxjs/operators'
import { ESResult } from '@tv3/store/search/search-result.model'
import { SearchResultType } from '@tv3/layout/navbar/search/search-result-type'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import * as lodash from 'lodash'
import { UntypedFormControl } from '@angular/forms'
import {
  OpenGuestOverlay,
  OpenInquiryOverlay,
  OpenInvoiceOverlay,
  OpenRentalOverlay,
} from '@tv3/store/overlay/overlay.actions'
import { ActionFailed, Destroyable, isSomething, untilDestroy } from '@tokeet-frontend/tv3-platform'
import { CalendarEventTypesMapping } from '@tv3/store/calendar/calendar.model'
import { HoldDialogService } from '@tv3/containers/calendar/hold/hold-dialog/hold-dialog.service'
import { AIService } from '@tokeet-frontend/ai'
import { SearchResultsDialogService } from '../search-results-dialog/search-results-dialog.service'
import { CalendarEvent } from '@tv3/store/calendar/calendar.model'
import { selectActiveProductPlanView } from '@tv3/store/plan/plan.selectors'
import { ProductsForPlan } from '@tv3/store/plan/plan.model'
import { AppOverviewDialogService } from '@tokeet-frontend/billing'
import { LoadActivePlans } from '@tv3/store/plan/plan.actions'
import { SearchService } from '@tv3/store/search/search.service'
@Component({
  selector: 'app-search',
  templateUrl: './search.component.html',
  host: { class: 'search-holder' },
  styleUrls: ['./search.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class SearchComponent extends Destroyable implements OnInit {
  @ViewChild('searchInput', { static: false }) searchInput

  resultsByType: Record<string, ESResult[]>

  types = SearchResultType

  isFocused = false
  isMenuOpen = false
  isLoading = false
  get isResultsEmpty() {
    return !isSomething(this.resultsByType)
  }

  searchCtrl = new UntypedFormControl('')
  calendarTypes = CalendarEventTypesMapping

  suggestQuestions = [
    'Find invoices that are overdue.',
    'Find invoices that are due next week.',
    'Which invoices require payment?',
    'Which hold events are active today?',
    'Which bookings are coming up for this week?',
    'Show me check-ins scheduled for tomorrow.',
    'List all upcoming check-outs for next weekend.',
    'Which bookings were canceled since the year start?',
    'How many confirmed bookings do I have from Tokeet in the last month?',
  ]
  randomQuestions: string[] = []

  assistantMessage = $localize`No data available, please try again later.`

  aiSub$ = this.store.pipe(
    select(selectActiveProductPlanView(ProductsForPlan.TokeetAI)),
    map((res) => res || {})
  )

  constructor(
    private aiService: AIService,
    private store: Store<fromRoot.State>,
    private holdDialog: HoldDialogService,
    private searchService: SearchService,
    private searchResultsDialog: SearchResultsDialogService,
    private appOverviewDialog: AppOverviewDialogService
  ) {
    super()
  }

  ngOnInit() {
    this.onRandomQuestions()
    this.searchCtrl.valueChanges
      .pipe(
        tap((term) => {
          if (!isSomething(term)) {
            this.resultsByType = null
          }
          this.onRandomQuestions(term)
        }),
        filter(isSomething),
        debounceTime(1000),
        tap(() => {
          this.isLoading = true
          this.isMenuOpen = true
        }),
        switchMap((term) =>
          this.aiSub$.pipe(
            switchMap((aiSub: any) => {
              if (aiSub?.isValid) {
                return this.aiService.search(term).pipe(
                  map(({ replies }) => {
                    const searchResult = replies.filter((r) => r.role === 'system')
                    const data = lodash.flatten(
                      searchResult.map((t) => t.metadata?.map((t) => ({ ...t.entity, object: t.type })) || [])
                    )
                    this.assistantMessage =
                      replies.find((r) => r.role === 'assistant')?.content || this.assistantMessage
                    return data
                  })
                )
              }
              return this.searchService.searchRaw(term).pipe(
                map((res) => {
                  return res
                    .map((r: any) => r._source)
                    .map((r: any) => ({
                      ...r,
                      object: r.object === SearchResultType.Hold2 ? SearchResultType.Hold : r.object,
                    }))
                })
              )
            })
          )
        ),
        tap(() => (this.isLoading = false)),
        untilDestroy(this)
      )
      .subscribe(
        (data) => {
          this.resultsByType = lodash.groupBy(data, (t) => t.object)
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
          this.isLoading = false
        }
      )
  }

  onVoice(data: Blob) {
    this.onTranscript(data)
  }

  onTranscript(data: Blob) {
    this.isLoading = true
    this.aiService.transcript(data).subscribe(
      (res) => {
        this.searchCtrl.setValue(res.text)
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
        this.isLoading = false
      }
    )
  }

  onRandomQuestions(term?: string) {
    let items = this.suggestQuestions
    if (term) {
      items = items.filter((f) => lodash.toLower(f).indexOf(term) !== -1)
    }

    this.randomQuestions = items.sort(() => Math.random() - 0.5).slice(0, 4)
  }

  onSearchClicked() {
    // if (this.searchCtrl.value) {
    this.isMenuOpen = true
    // }
  }

  onSearch(term: string) {
    this.searchCtrl.setValue(term)
    this.isMenuOpen = true
  }

  guestSource(source: string) {
    if (isSomething(source)) {
      return `from ${source} `
    } else {
      return 'from AdvanceCM '
    }
  }

  onEnableAI() {
    this.appOverviewDialog
      .open(ProductsForPlan.TokeetAI)
      .afterClosed()
      .subscribe((res) => {
        this.store.dispatch(LoadActivePlans({}))
      })
  }

  onClearSearch() {
    this.searchCtrl.patchValue('')
  }

  onInquiryDetails(inquiryId: string) {
    this.store.dispatch(OpenInquiryOverlay({ inquiryId }))
  }

  onGuestDetails(guestId: string) {
    this.store.dispatch(OpenGuestOverlay({ guestId }))
  }

  onInvoiceDetails(invoiceId: string) {
    this.store.dispatch(OpenInvoiceOverlay({ invoiceId }))
  }

  onHoldDetails(data) {
    this.holdDialog.open({ hold: CalendarEvent.deserialize(data) })
  }

  onRentalDetails(rentalId: string) {
    this.store.dispatch(OpenRentalOverlay({ rentalId }))
  }

  openSearchResultsDialog(type: SearchResultType) {
    if (!this.resultsByType || !this.resultsByType[type]) return
    this.searchResultsDialog.open(type, this.resultsByType[type])
  }
}
